import { FIFTY_FIFTY_NINE_PRODUCT } from "src/views/products/fifty-fifty-nine/fiftyFiftyNineProduct";
import {
  ITEM_EQUIVALENCY_PRODUCT,
  itemEquivalencyChatUrl,
} from "src/views/products/item-equivalency/itemEquivalencyProduct";
import { PROPOSAL_GENERATION_PRODUCT } from "src/views/products/proposal-generation/proposalGenerationProduct";
import { folderChatPath } from "src/views/assistant/utils/projectFileRoute";

import { registerNotificationKind } from "../registry";
import type { UserNotification } from "../types";

/**
 * `chat_status`: published by the agent service around every chat turn
 * (mre/agent_service/session.py) with `resource_type: "dialogue"`,
 * `data.product` naming the product the dialogue belongs to and
 * `data.project_folder_id` the project to open it in (null when unanchored or
 * not readable). Every product chat (Parts AI, 50.59, Proposal Gen, ...) runs
 * through the same session, so the title names the product and the link is
 * only set where the route is known.
 */
export type ChatStatus = "working" | "completed" | "error" | "cancelled";

/**
 * Keyed by the backend product slug (`conversation.product`), which for the
 * workflow products is the descriptor's `productKey`, not its
 * `licenseModule` (50.59 runs under the licensing-compliance licence but
 * sends "fifty-fifty-nine-evaluation"). Reading the key and the name off the
 * descriptor keeps this map from drifting when a product is renamed.
 */
const PRODUCT_LABELS: Record<string, string> = {
  "atom-assist": "AtomAssist",
  "parts-ai": "Parts AI",
  "equipment-ai": "Equipment AI",
  "vnv-assist": "V&V Assist",
  [FIFTY_FIFTY_NINE_PRODUCT.productKey]: FIFTY_FIFTY_NINE_PRODUCT.displayName,
  [ITEM_EQUIVALENCY_PRODUCT.productKey]: ITEM_EQUIVALENCY_PRODUCT.displayName,
  [PROPOSAL_GENERATION_PRODUCT.productKey]:
    PROPOSAL_GENERATION_PRODUCT.displayName,
};

/**
 * Chat routes by product. Products not listed open nothing (no link): the
 * products whose chat URL needs a fetch (Parts AI, Proposal Generation, V&V)
 * only have async builders, and a notification link has to be synchronous.
 */
const CHAT_ROUTES: Record<
  string,
  (dialogueId: string, n: UserNotification) => string
> = {
  // A project chat opens inside its project. The backend sends
  // `project_folder_id` only for a folder the user could read when the session
  // started; null (or any non-number) falls back to the main chat page.
  "atom-assist": (id, n) =>
    typeof n.data.project_folder_id === "number"
      ? folderChatPath(n.data.project_folder_id, Number(id))
      : `/assistant/chat/${id}`,
  // Resolves the dialogue's persisted scope to its asset/location page.
  "equipment-ai": (id) => `/products/equipment-ai/chat/${id}`,
  [ITEM_EQUIVALENCY_PRODUCT.productKey]: (id) =>
    itemEquivalencyChatUrl(Number(id)),
};

export const chatProduct = (n: UserNotification): string =>
  typeof n.data.product === "string" && n.data.product
    ? n.data.product
    : "atom-assist";

const label = (n: UserNotification) =>
  PRODUCT_LABELS[chatProduct(n)] ?? "Assistant";

registerNotificationKind("chat_status", {
  title: (n) => {
    switch (n.status as ChatStatus | null) {
      case "completed":
        return `${label(n)} finished responding`;
      case "error":
        return `${label(n)} hit an error`;
      case "cancelled":
        return `${label(n)} response stopped`;
      case "working":
        return `${label(n)} is working`;
      default:
        // A status this build doesn't know: say what we got rather than
        // guess that it's still in progress.
        return `${label(n)}: ${n.status ?? "update"}`;
    }
  },
  // completed: the backend's excerpt / one-line digest; error: the
  // sanitized reason, with a nudge when there isn't one.
  message: (n) =>
    n.status === "error" ? (n.message ?? "Open the chat to retry.") : n.message,
  link: (n) => {
    if (!n.resource_id) return null;
    return CHAT_ROUTES[chatProduct(n)]?.(n.resource_id, n) ?? n.link;
  },
});
